import type { Account, AccountWithUsers, Office, ResourceUser, User } from '@services/dto/account';
import type { ProEstEstimate, ProEstEstimateDetails } from '@services/dto/proEst';
import type {
  Estimate,
  EstimateLabor,
  ExcelEstimateDetails,
  Phase,
  Project,
  ProjectCounts,
  ProjectStatus,
} from '@services/dto/project';

export type ProjectEx = Omit<Project, 'account' | 'phases'> & {
  account: Account;
  /** The client company's roster. Absent when the reader may not read it - a guest, or a refusal. */
  accountWithUsers?: AccountWithUsers;
  provider: Account;
  /** The provider company's roster. Absent when the reader may not read it. */
  providerAccountWithUsers?: AccountWithUsers;
  isGuest: boolean;
  canEdit: boolean;
  canEditPhase: boolean;
  canComplete: boolean;
  canDelete: boolean;
  canSubmit: boolean;
  canInvite: boolean;
  canViewStaff?: boolean;
  isAssignedPm?: boolean;
  phases: PhaseEx[];
  asyncUpdate?: boolean;
  expertOffer?: boolean;
  expertOfferPending?: boolean;
  clientPm?: User;
  providerPm?: User;
  providerOffice?: Office | null;
  providerOfficeId?: string;
  drawingSummaries?: { phaseId: string; summary: string }[];
  basisOfEstimateItems?: { phaseId: string; basisOfEstimate: string }[];
  estimationTool?: 'proest' | 'excel';
  resourceUsers: ResourceUser[];
  providerPmUserId?: string;
  lastFeeUpdateNotificationSentAt?: string;
};

export type PhaseEx = Omit<Phase, 'estimates'> & {
  isVisible?: boolean;
  isGuest?: boolean;
  canEdit?: boolean;
  canStart?: boolean;
  canComplete?: boolean;
  canDelete?: boolean;
  canSubmit?: boolean;
  canInvite?: boolean;
  canViewStaff?: boolean;
  estimates?: EstimateEx[];
  canPayInvoice?: boolean;
};

export type EstimateEx = Omit<Estimate, 'labor'> & {
  labor: EstimateLaborEx[];
  isVisible?: boolean;
  isGuest: boolean;
  canEdit?: boolean;
};

export type EstimateLaborEx = EstimateLabor;

export type ProEstState = {
  estimate: ProEstEstimate;
  details: ProEstEstimateDetails;
};

export interface ProjectName {
  id?: string;
  status?: ProjectStatus;
  name?: string;
}

export type ProjectState = {
  loading?: boolean;
  project?: ProjectEx;
  // biome-ignore lint/suspicious/noExplicitAny: Loose type.
  error?: any;

  proEst?: ProEstState;
  excel?: ExcelEstimateDetails;

  counts: ProjectCounts[];
  monthlyCounts: ProjectCounts[];

  projectNames?: ProjectName[];
};
